import type { TelecomTokenService } from "../../entities/user/telecom-token-service";
import type {
  GetCallHistoryResponse,
  GetCallRatesResponse,
  GetSmsHistoryResponse,
  GetSmsRatesResponse,
  SendSmsResponse,
  CallRate,
  SmsRate,
  GetLocalBundlesResponse,
  LinkNumberResponse,
  SetCoupleResponse,
  SetCallDstResponse,
  SetSmsDstResponse,
} from "./telecom-types";
import { customFetcher, HttpError, isHttpError } from "./http";

/**
 * Custom error class for Telecom API errors
 */
export class TelecomApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = "TelecomApiError";
  }
}

/**
 * Configuration for Telecom API client
 */
export interface TelecomApiConfig {
  baseUrl: string;
  timeout?: number;
}

/**
 * Service for making requests to Telecom API with automatic token revalidation.
 * Handles 401 errors by refreshing the token and retrying the request.
 */
export class TelecomApiService {
  private revalidatingToken: Promise<string | null> | null = null;

  constructor(
    private readonly config: TelecomApiConfig,
    private readonly tokenService: TelecomTokenService,
  ) {}

  /**
   * Get telecom API base URL from config
   */
  private getBaseUrl(): string {
    return this.config.baseUrl;
  }

  /**
   * Ensure we have a valid token, revalidating if needed
   */
  private async ensureValidToken(): Promise<string> {
    const token = await this.tokenService.ensureToken();
    if (!token) {
      throw new TelecomApiError(
        "Telecom token not available",
        401,
        "token_not_available",
      );
    }
    return token;
  }

  /**
   * Revalidate token by fetching a new one from backend
   */
  private async revalidateToken(): Promise<string | null> {
    // Prevent multiple concurrent revalidation requests
    if (this.revalidatingToken) {
      return this.revalidatingToken;
    }

    this.revalidatingToken = (async () => {
      try {
        const token = await this.tokenService.fetchAndStoreToken();
        return token;
      } catch (error) {
        console.error("Failed to revalidate telecom token:", error);
        await this.tokenService.clearToken();
        return null;
      } finally {
        this.revalidatingToken = null;
      }
    })();

    return this.revalidatingToken;
  }

  /**
   * Make a request to Telecom API with automatic token revalidation on 401
   * Uses customFetcher for consistent error handling and retry logic
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryOn401 = true,
  ): Promise<T> {
    const baseUrl = this.getBaseUrl();
    const url = endpoint.startsWith("http")
      ? endpoint
      : `${baseUrl.replace(/\/$/, "")}/${endpoint.replace(/^\//, "")}`;

    const token = await this.ensureValidToken();
    const tokenString = typeof token === 'string' ? token : String(token);

    const method = options.method || "GET";
    
    // Parse body if it's a string (JSON)
    let requestData: unknown = undefined;
    if (options.body) {
      if (typeof options.body === 'string') {
        try {
          requestData = JSON.parse(options.body);
        } catch {
          requestData = options.body;
        }
      } else {
        requestData = options.body;
      }
    }

    // Log request details
    console.log(`[Telecom API] ${method} ${url}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        "X-Auth-Bearer": tokenString ? `${tokenString.substring(0, 10)}...` : "missing",
      },
      body: requestData ? (typeof requestData === 'string' ? requestData.substring(0, 200) : "[Body]") : undefined,
    });

    // Create request object with headers that can be updated on retry
    const requestHeaders: Record<string, string> = {
      "X-Auth-Bearer": tokenString,
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    try {
      const result = await customFetcher<T>({
        url,
        method,
        data: requestData,
        headers: requestHeaders,
      }, {
        baseUrl: "", // URL is already absolute or includes base
        onRefresh: retryOn401 ? async () => {
          console.log("Telecom API returned 401, revalidating token...");
          const newToken = await this.revalidateToken();
          if (!newToken) {
            throw new TelecomApiError(
              "Failed to revalidate telecom token",
              401,
              "token_revalidation_failed",
            );
          }
          // Update token in headers for retry
          const newTokenString = typeof newToken === 'string' ? newToken : String(newToken);
          requestHeaders["X-Auth-Bearer"] = newTokenString;
          return true;
        } : undefined,
        // Don't use getToken for Telecom API - we use X-Auth-Bearer header directly
      });

      // Log success
      const responsePreview = JSON.stringify(result).length > 500
        ? `${JSON.stringify(result).substring(0, 500)}... [truncated]`
        : JSON.stringify(result);
      console.log(`[Telecom API] Response success:`, responsePreview);

      return result;
    } catch (error) {
      // Convert HttpError to TelecomApiError for consistency
      if (isHttpError(error)) {
        // Try to extract error code from error data
        let errorCode: string | undefined;
        let errorMessage = error.message;

        if (error.data && typeof error.data === 'object') {
          const errorData = error.data as Record<string, unknown>;
          if (errorData.error) {
            errorMessage = String(errorData.error);
            if (typeof errorData.error === 'string' && errorData.error.toUpperCase() === errorData.error) {
              errorCode = errorData.error;
            }
          }
          if (errorData.code) {
            errorCode = String(errorData.code);
          }
        }

        // Handle empty response case
        if (error.status === 200 && errorMessage.includes('Empty')) {
          throw new TelecomApiError(
            "Empty response from server",
            error.status,
            "empty_response",
          );
        }

        throw new TelecomApiError(errorMessage, error.status, errorCode);
      }

      // Re-throw TelecomApiError as-is
      if (error instanceof TelecomApiError) {
        throw error;
      }

      // Wrap unknown errors
      throw new TelecomApiError(
        error instanceof Error ? error.message : "Unknown error",
        500,
        "unknown_error",
      );
    }
  }

  /**
   * Get call history for user
   * @param msisdn Phone number (required)
   * @param bNumber Optional filter by recipient number
   */
  async getCallHistory(msisdn: string, bNumber?: string): Promise<GetCallHistoryResponse> {
    const params = new URLSearchParams({ msisdn });
    if (bNumber) {
      params.set("b_number", bNumber);
    }
    return this.request<GetCallHistoryResponse>(`getCallHistory?${params.toString()}`, {
      method: "GET",
    });
  }

  /**
   * Send SMS
   * @param from Sender phone number
   * @param to Recipient phone number
   * @param text Message text
   */
  async sendSMS(
    from: string,
    to: string,
    text: string,
  ): Promise<SendSmsResponse> {
    return this.request<SendSmsResponse>("sendSMS", {
      method: "POST",
      body: JSON.stringify({ from, to, text }),
    });
  }

  /**
   * Get SMS history for user
   * @param msisdn Phone number (required)
   * @param bNumber Optional filter by recipient number
   */
  async getSMSHistory(msisdn: string, bNumber?: string): Promise<GetSmsHistoryResponse> {
    const params = new URLSearchParams({ msisdn });
    if (bNumber) {
      params.set("b_number", bNumber);
    }
    return this.request<GetSmsHistoryResponse>(`getSMSHistory?${params.toString()}`, {
      method: "GET",
    });
  }

  /**
   * Get call rate for specific number
   * @param from Sender phone number
   * @param to Recipient phone number
   */
  async getCallRate(from: string, to: string): Promise<CallRate> {
    return this.request<CallRate>(
      `getCallRate?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      {
        method: "GET",
      },
    );
  }

  /**
   * Get all call rates
   */
  async getCallRates(): Promise<GetCallRatesResponse> {
    return this.request<GetCallRatesResponse>("getCallRates", {
      method: "GET",
    });
  }

  /**
   * Get SMS rate for specific number
   * @param from Sender phone number
   * @param to Recipient phone number
   */
  async getSMSRate(from: string, to: string): Promise<SmsRate> {
    return this.request<SmsRate>(
      `getSMSRate?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      {
        method: "GET",
      },
    );
  }

  /**
   * Get all SMS rates
   */
  async getSMSRates(): Promise<GetSmsRatesResponse> {
    return this.request<GetSmsRatesResponse>("getSMSRates", {
      method: "GET",
    });
  }

  /**
   * Link Telegram number to MSISDN
   * @param msisdn Phone number
   * @param telegramNumber Telegram phone number (or "destroy" to unlink)
   */
  async linkNumber(
    msisdn: string,
    telegramNumber: string,
  ): Promise<LinkNumberResponse> {
    return this.request<LinkNumberResponse>("linkNumber", {
      method: "POST",
      body: JSON.stringify({ msisdn, telegramNumber }),
    });
  }

  /**
   * Add or remove outbound number for calls
   * @param msisdn Main phone number
   * @param outboundNumber Outbound number to add/remove
   * @param add true to add, false to remove
   */
  async setCouple(
    msisdn: string,
    outboundNumber: string,
    add: boolean,
  ): Promise<SetCoupleResponse> {
    return this.request<SetCoupleResponse>("setCouple", {
      method: "POST",
      body: JSON.stringify({ msisdn, outboundNumber, add }),
    });
  }

  /**
   * Set call destination (eSIM or Telegram)
   * @param msisdn Phone number
   * @param dst Destination: "S" for eSIM, "T" for Telegram
   */
  async setCallDst(msisdn: string, dst: "S" | "T"): Promise<SetCallDstResponse> {
    return this.request<SetCallDstResponse>("setCallDst", {
      method: "POST",
      body: JSON.stringify({ msisdn, dst }),
    });
  }

  /**
   * Set SMS destination (eSIM, Telegram, or both)
   * @param msisdn Phone number
   * @param dst Destination: "S" for eSIM, "T" for Telegram, "B" for both
   */
  async setSMSDst(
    msisdn: string,
    dst: "S" | "T" | "B",
  ): Promise<SetSmsDstResponse> {
    return this.request<SetSmsDstResponse>("setSMSDst", {
      method: "POST",
      body: JSON.stringify({ msisdn, dst }),
    });
  }

  /**
   * Get available local bundles
   */
  async getLocalBundles(): Promise<GetLocalBundlesResponse> {
    return this.request<GetLocalBundlesResponse>("getLocalBandles", {
      method: "GET",
    });
  }
}

